import { MessageOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Point,
    SubtaskProductsMatrix,
    TaskAssortmentMatrix,
    TaskStatus,
} from "tm-shared/types";

import { useCatalog } from "@/domains/product";
import {
    CommentModal,
    StatusCycleButton,
    TaskInfoModal,
    useActiveScrollNavigator,
    useCheckCommentOnReject,
    useStickyFilteredItems,
    useSubtaskConfigSelector,
    useSubtaskProductsMatrixSelector,
} from "@/domains/tasks";
import { useAppDispatch } from "@/hooks/redux";
import { BottomSheet } from "@/shared/ui/BottomSheet";
import { OutlineIconButton } from "@/shared/ui/OutlineIconButton";
import { PrimaryText } from "@/shared/ui/Typography";
import { patchProductsMatrixEntity } from "@/store/slices/progress";
import { setMdTasksConfig } from "@/store/slices/ui";

import { useAssortmentMatrixCompletion } from "../hooks/useAssortmentMatrixCompletion";
import { StepButton } from "../StepButton";
import { StyledBottomSheetTop } from "../Styled";
import { StyledList } from "../Styled";
import { ProductsMatrixTask } from "./components/ProductsMatrixTask";
import { StyledPMMainContainer } from "./Styled";

interface ProductsMatrixProps {
    subtask: SubtaskProductsMatrix;
    point: Point;
    task: TaskAssortmentMatrix;
}

interface ProductInfo {
    barcode: string;
    name: string;
    finGroup?: string;
}

export const ProductsMatrix: FC<ProductsMatrixProps> = ({
    subtask,
    point,
    task,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [modal, setModal] = useState<"comment" | "taskInfo" | null>(null);

    const { products } = useCatalog();

    const pointId = point.id;
    const taskId = task.id;
    const subtaskId = subtask.subtaskId;
    const contractId = subtask.contractId;

    const previousActiveProductRef = useRef<string | null>(null);

    const productsMatrixData = useSubtaskProductsMatrixSelector({
        taskId,
        subtaskId,
    });

    const {
        activeItem: activeProductBarcode = null,
        showUncompleted: showUncompletedFilter,
        chosenFingroup: activeFinGroup,
    } = useSubtaskConfigSelector({
        pointId,
        taskId,
        subtaskId,
    });

    const productBarcodes = useMemo(
        () => Array.from(new Set(subtask.context.products)),
        [subtask.context.products],
    );

    const productsInfo = useMemo<ProductInfo[]>(
        () =>
            productBarcodes.map((barcode) => {
                const product = products[barcode];
                return {
                    barcode,
                    name: product?.name ?? "Невідомий товар",
                    finGroup: product?.params?.fin_group_detail?.value,
                };
            }),
        [products, productBarcodes],
    );

    const { filteredItems: filteredProducts } = useStickyFilteredItems({
        items: productsInfo,
        showUncompleted: showUncompletedFilter ?? false,
        getItemId: (product) => product.barcode,
        payloadsMap: productsMatrixData?.products ?? {},
        additionalFilter: activeFinGroup
            ? (product) => product.finGroup === activeFinGroup
            : undefined,
        isIncomplete: (payload) => {
            return !payload || payload.status === TaskStatus.Incomplete;
        },
    });

    const activePayload = useMemo(
        () =>
            activeProductBarcode
                ? {
                      barcode: activeProductBarcode,
                      name:
                          products[activeProductBarcode]?.name ??
                          activeProductBarcode,
                      payload:
                          productsMatrixData?.products?.[
                              activeProductBarcode
                          ] ?? null,
                      status:
                          productsMatrixData?.products?.[activeProductBarcode]
                              ?.status ?? TaskStatus.Incomplete,
                  }
                : null,
        [activeProductBarcode, products, productsMatrixData],
    );

    const resetStatusIfNeeded = useCallback(
        (barcode: string) => {
            const productPayload = productsMatrixData?.products?.[barcode];

            if (!productPayload) return;
            if (
                productPayload.status === TaskStatus.Rejected &&
                !productPayload.comment
            ) {
                dispatch(
                    patchProductsMatrixEntity({
                        taskId,
                        subtaskId,
                        barcode,
                        contractId,
                        patch: { status: TaskStatus.Incomplete },
                    }),
                );
            }
        },
        [dispatch, taskId, subtaskId, contractId, productsMatrixData],
    );

    const handleCloseBottomSheet = useCallback(() => {
        dispatch(
            setMdTasksConfig({
                pointId,
                taskId,
                subtaskId,
                value: null,
            }),
        );
    }, [dispatch, pointId, taskId, subtaskId]);

    const handleOpenCommentModal = useCallback(() => {
        if (!activePayload || activePayload.status === TaskStatus.Incomplete)
            return;

        setModal("comment");
    }, [activePayload]);

    const handleChangeComment = useCallback(
        (newComment: string) => {
            if (!activePayload) return;

            dispatch(
                patchProductsMatrixEntity({
                    taskId,
                    subtaskId,
                    contractId,
                    barcode: activePayload.barcode,
                    patch: { comment: newComment },
                }),
            );
        },
        [activePayload, contractId, dispatch, subtaskId, taskId],
    );

    const handleOpenTaskInfoModal = () => {
        setModal("taskInfo");
    };

    const handleChangeActiveProduct = useCallback(
        (barcode: string) => {
            dispatch(
                setMdTasksConfig({
                    pointId,
                    taskId,
                    subtaskId,
                    value: barcode,
                }),
            );
        },
        [dispatch, pointId, taskId, subtaskId],
    );

    const { validateAndNotify } = useCheckCommentOnReject();

    const handleCheckCommentOnReject = useCallback(
        (barcode: string | null) => {
            const previousBarcode = previousActiveProductRef.current ?? "";
            const productPayload =
                productsMatrixData?.products?.[previousBarcode];

            if (validateAndNotify(productPayload)) {
                return;
            }

            if (barcode) handleChangeActiveProduct(barcode);
        },
        [
            productsMatrixData?.products,
            validateAndNotify,
            handleChangeActiveProduct,
        ],
    );

    const handleChangeTaskStatus = useCallback(
        (status: TaskStatus, barcode?: string) => {
            const targetBarcode = barcode ?? activePayload?.barcode;

            if (!targetBarcode) return;

            dispatch(
                patchProductsMatrixEntity({
                    taskId,
                    subtaskId,
                    contractId,
                    barcode: targetBarcode,
                    patch: { status, comment: "" },
                }),
            );
        },
        [dispatch, taskId, subtaskId, contractId, activePayload],
    );

    const navigateToProductPage = useCallback(
        (barcode: string) => {
            void navigate(`/work-plan/product-card/${barcode}`);
        },
        [navigate],
    );

    useEffect(() => {
        const previousBarcode = previousActiveProductRef.current;

        if (previousBarcode && activeProductBarcode !== previousBarcode) {
            resetStatusIfNeeded(previousBarcode);
        }

        previousActiveProductRef.current = activeProductBarcode;
    }, [activeProductBarcode, resetStatusIfNeeded]);

    const { getItemRef, onNext, onPrev, scrollToActive } =
        useActiveScrollNavigator({
            ids: filteredProducts.map((p) => p.barcode),
            activeId: activeProductBarcode ?? null,
            onChangeActive: handleCheckCommentOnReject,
        });

    useAssortmentMatrixCompletion({
        taskId,
        subtaskId,
        expectedCount: productBarcodes?.length ?? 0,
        payloadsMap: productsMatrixData?.products ?? null,
    });

    useEffect(() => {
        scrollToActive();
    }, [productsMatrixData?.completed, scrollToActive]);

    return (
        <>
            <StyledPMMainContainer>
                <StyledList>
                    {filteredProducts.map((productInfo) => {
                        const productPayload =
                            productsMatrixData?.products?.[
                                productInfo.barcode
                            ] ?? null;
                        const isActive =
                            productInfo.barcode === activeProductBarcode;

                        return (
                            <div
                                key={productInfo.barcode}
                                ref={getItemRef(productInfo.barcode)}
                            >
                                <ProductsMatrixTask
                                    barcode={productInfo.barcode}
                                    name={productInfo.name}
                                    isActive={isActive}
                                    onChangeActive={handleChangeActiveProduct}
                                    payload={productPayload}
                                    onChangeStatus={handleChangeTaskStatus}
                                />
                            </div>
                        );
                    })}
                </StyledList>

                <StepButton
                    task={task}
                    pointId={pointId}
                    currentCompleted={productsMatrixData?.completed}
                />
            </StyledPMMainContainer>

            <BottomSheet
                open={!!activeProductBarcode}
                onClose={handleCloseBottomSheet}
                onNext={onNext}
                onPrev={onPrev}
                onClickInfoButton={handleOpenTaskInfoModal}
            >
                <StyledBottomSheetTop>
                    <PrimaryText
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                            activePayload &&
                            navigateToProductPage(activePayload.barcode)
                        }
                    >
                        {activePayload?.name}
                    </PrimaryText>
                    <Flex gap="20px" justify="center" align="center">
                        <OutlineIconButton
                            icon={<MessageOutlined />}
                            onClick={handleOpenCommentModal}
                            disabled={
                                !activePayload?.status ||
                                activePayload.status === TaskStatus.Incomplete
                            }
                        />
                        <StatusCycleButton
                            value={
                                activePayload?.status ?? TaskStatus.Incomplete
                            }
                            onChange={handleChangeTaskStatus}
                        />
                    </Flex>
                </StyledBottomSheetTop>
            </BottomSheet>

            {modal === "comment" && activePayload && (
                <CommentModal
                    onCloseModal={() => setModal(null)}
                    onConfirm={handleChangeComment}
                    initialComment={activePayload.payload?.comment ?? ""}
                    taskStatus={activePayload.status}
                />
            )}

            {modal === "taskInfo" && (
                <TaskInfoModal
                    onCloseModal={() => setModal(null)}
                    title={subtask.name || "Матриця товарів"}
                    description={
                        subtask.description ||
                        "Оберіть статус для кожного товару"
                    }
                />
            )}
        </>
    );
};
